/*
 * This component just show fields for customer redis configuration form.
 *
 * Used by: customer -> edit customer -> redis configuration tab
 */
import React, { Fragment, useCallback, useState } from 'react';
import useToggle from './hooks/useToggle.jsx';
import { Button, Segment } from 'semantic-ui-react';
import * as uuid from 'uuid';
import styledComponents from 'styled-components';
import DynamicField from './field/DynamicField.jsx';
import Messenger from '../views/components/messenger.js';

const ErrorMessage = styledComponents.span`
  color: #9f3a38;
  display: block;
`;

const SuccessMessage = styledComponents.span`
  color: #1a531b;
  display: block;
`;

const InputRow = ({
  displayName,
  inputName,
  value,
  inputType,
  id,
  inputFor,
  getInputName,
  errors = [],
  required = false,
  onValue,
  help,
  ...props
}) => {
  const snakeCaseName = displayName.replace(/\s/g, '_').toLowerCase();
  const inputID = id ? `${inputFor}_${snakeCaseName}_${id}` : uuid.v4();

  let displayValue = value;

  if (inputType === 'boolean') {
    displayValue = displayValue ? 'Enabled' : 'Disabled';
  }

  return (
    <tr className={required ? 'required' : ''}>
      <td className='name'>
        <label htmlFor={inputID}>{displayName}:</label>
        <p className='help'>{help}</p>
      </td>
      <td className='control property'>
        <div
          key='1'
          className='hide-disabled'
        >
          <DynamicField
            id={inputID}
            name={inputName || getInputName(snakeCaseName)}
            type={inputType}
            value={value}
            onValue={onValue}
            {...props}
          />
          {errors.map((error, idx) => (
            <div
              key={`error-${idx}`}
              className='error'
            >
              {error}
            </div>
          ))}
        </div>
        <div
          key='2'
          className='show-disabled'
        >
          {displayValue}
        </div>
      </td>
    </tr>
  );
};

module.exports = function RedisConfig({
  id = '',
  host = 'localhost',
  port = 6379,
  dbNumber = 0,
  hideDbNumber = false,
  errors = {},
  inputNamePrefix = 'customer',
  inputFor = 'redis_configs_attributes',
  tls = false,
  password = '',
  csrfToken,
  currentCustomer,
}) {
  const destroyed = useToggle(false);
  const getInputName = useCallback(
    (field) => `${inputNamePrefix}[${inputFor}][][${field}]`,
    [],
  );
  const [hostValue, setHost] = useState(host);
  const [portValue, setPort] = useState(port);
  const [dbValue, setDB] = useState(dbNumber);
  const [tlsValue, setTLS] = useState(tls);
  const [passwordValue, setPassword] = useState(password);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const testConnection = () => {
    setSuccess(false);
    setError(false);
    setLoading(true);

    let attributes = {
      host: hostValue,
      port: portValue,
      tls: tlsValue,
      password: passwordValue,
    };

    if (!hideDbNumber) {
      attributes.db_number = dbValue;
    }
    if (hostValue === host) {
      attributes.id = id;
    }

    Messenger.on(`/${currentCustomer}/customers/${currentCustomer}`, (data) => {
      if (data.error) {
        setError(data.error);
      } else {
        setSuccess(data.success);
      }
      setLoading(false);
      Messenger.unsubscribe(`/${currentCustomer}/customers/${currentCustomer}`);
    });

    fetch(`/${currentCustomer}/customers/${currentCustomer}/test_redis`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        customer: {
          redis_configs_attributes: attributes,
        },
      }),
    }).catch(() => {
      setError('There was an error trying to test the connection');
    });
  };

  if (destroyed.value && !id) {
    return null;
  }

  return (
    <Fragment>
      {id && (
        <input
          name={getInputName('id')}
          type='hidden'
          value={id}
        />
      )}
      {destroyed.value ? (
        <input
          name={getInputName('_destroy')}
          type='hidden'
          value='1'
        />
      ) : (
        <Segment>
          <table className='edit_form io-form-table'>
            <tbody>
              <InputRow
                displayName='Host'
                errors={errors.host}
                getInputName={getInputName}
                help={'Enter IP address or name of redis server'}
                id={id}
                inputFor={inputFor}
                inputType='text'
                value={hostValue}
                onValue={(value) => setHost(value)}
              />
              <InputRow
                displayName='Port'
                errors={errors.port}
                getInputName={getInputName}
                help={'Enter port number to use'}
                id={id}
                inputFor={inputFor}
                max={65536}
                min={0}
                required={true}
                value={portValue}
                onValue={(value) => setPort(value)}
              />
              {!hideDbNumber && (
                <InputRow
                  displayName='Database Number'
                  errors={errors.db_number}
                  getInputName={getInputName}
                  help={'Enter database number to connect to, if necessary'}
                  id={id}
                  inputFor={inputFor}
                  inputType='number'
                  max={1000}
                  min={0}
                  value={dbValue}
                  onValue={(value) => setDB(value)}
                />
              )}
              <InputRow
                displayName='Token'
                errors={errors.password}
                help={'Generate Password if one is not populated already'}
                id={id}
                inputFor={inputFor}
                inputName={getInputName('password')}
                inputType='password'
                value={passwordValue}
                onValue={(value) => setPassword(value)}
              />
              <InputRow
                displayName='TLS'
                errors={errors.tls}
                getInputName={getInputName}
                help={
                  'Chose enabled or disabled depending on your redis server setup'
                }
                id={id}
                inputFor={inputFor}
                inputType='boolean'
                value={tlsValue}
                onValue={(value) => setTLS(value)}
              />
              <tr>
                <td className='name'></td>
                <td className='control'>
                  <Button
                    as='a'
                    content='Test Connection'
                    disabled={loading}
                    loading={loading}
                    onClick={testConnection}
                  />
                  <If condition={success}>
                    <SuccessMessage>{success}</SuccessMessage>
                  </If>
                  <If condition={error}>
                    <ErrorMessage>{error}</ErrorMessage>
                  </If>
                </td>
              </tr>
            </tbody>
          </table>
          {hideDbNumber && (
            <input
              name={getInputName('db_number')}
              type='hidden'
              value={dbNumber}
            />
          )}
          <Button
            negative
            as='a'
            className='multiple-value-button'
            content='Remove'
            icon='minus circle'
            onClick={destroyed.setTrue}
          />
        </Segment>
      )}
    </Fragment>
  );
};
